import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  DispatchJob,
  DriverRow,
  useClaimDispatchJobMutation,
  useDeleteDispatchJobMutation,
  useGetDispatchJobsForPinManagementQuery,
  useGetDriversQuery,
  useUpdateDispatchJobStatusMutation,
  useVerifyDispatchJobDeliveryPinMutation,
} from "../Pins/pinManagementApi";
import axios from "axios";
import { getAuth } from "firebase/auth";
import {
  useAdminAccess,
  AdminAccessProvider,
} from "../Analytics/shared/AdminAccess/AdminAccessContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const formatMoney = (value?: number) =>
  `MWK ${Number(value || 0).toLocaleString()}`;

const normalizeStatus = (status?: string) =>
  String(status || "")
    .trim()
    .toLowerCase();

const prettifyStatus = (status?: string) =>
  String(status || "-")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusColorScheme = (status?: string) => {
  const normalized = normalizeStatus(status);

  if (["delivered", "returned_to_sender"].includes(normalized)) {
    return "green";
  }

  if (
    [
      "assigned",
      "arrived_at_pickup",
      "picked_up",
      "in_transit",
      "arrived_at_delivery",
      "proof_of_delivery_pending",
      "delivery_failed_customer_unavailable",
      "return_in_transit",
    ].includes(normalized)
  ) {
    return "blue";
  }

  if (["cancelled", "expired", "failed"].includes(normalized)) {
    return "red";
  }

  return "gray";
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  assigned: ["arrived_at_pickup", "cancelled"],
  arrived_at_pickup: ["picked_up", "cancelled"],
  picked_up: ["in_transit", "cancelled"],
  in_transit: ["arrived_at_delivery", "cancelled"],
  arrived_at_delivery: [
    "proof_of_delivery_pending",
    "delivery_failed_customer_unavailable",
    "cancelled",
  ],
  proof_of_delivery_pending: [
    "delivered",
    "delivery_failed_customer_unavailable",
    "cancelled",
  ],
  delivery_failed_customer_unavailable: [
    "arrived_at_delivery",
    "return_in_transit",
    "cancelled",
  ],
  return_in_transit: ["returned_to_sender", "cancelled"],
  returned_to_sender: [],
  delivered: [],
  cancelled: [],
  pending_dispatch: [],
  notified: [],
  claimed: [],
  expired: [],
};

const ALL_STATUSES = [
  "pending_dispatch",
  "notified",
  "claimed",
  "assigned",
  "arrived_at_pickup",
  "picked_up",
  "in_transit",
  "arrived_at_delivery",
  "proof_of_delivery_pending",
  "delivery_failed_customer_unavailable",
  "return_in_transit",
  "returned_to_sender",
  "delivered",
  "cancelled",
  "expired",
];

const CLAIMABLE_STATUSES = ["pending_dispatch", "notified", "claimed"];
const UNASSIGNABLE_DRIVER_STATUSES = new Set([
  "pending_dispatch",
  "notified",
  "claimed",
  "assigned",
  "arrived_at_pickup",
]);

type AccountOption = {
  id: string;
  label: string;
  email?: string;
};

const clean = (value: unknown) => String(value || "").trim();

const toArray = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;

  return [];
};

const getRecordAccountIds = (record: any) => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = clean(value);
    if (id) ids.add(id);
  };

  const addMany = (values: unknown) => {
    if (!Array.isArray(values)) return;
    values.forEach(add);
  };

  [
    record?.ownerUserId,
    record?.ownerId,
    record?.sellerUserId,
    record?.sellerId,
    record?.accountUserId,
    record?.businessUserId,
    record?.merchantUserId,
    record?.vendorUserId,
    record?.createdBy,
    record?.createdByUserId,
    record?.senderUserId,
    record?.requestedBy,
    record?.requesterUserId,
    record?.warehouseOwnerUserId,
    record?.sourceOwnerUserId,
    record?.managedByUserId,
    record?.registeredBy,
  ].forEach(add);

  [record?.accountUserIds, record?.ownerUserIds, record?.sellerUserIds].forEach(
    addMany,
  );

  const metadata = record?.metadata || {};
  const warehouse = record?.warehouse || {};

  [
    metadata?.ownerUserId,
    metadata?.ownerId,
    metadata?.sellerUserId,
    metadata?.sellerId,
    metadata?.accountUserId,
    metadata?.businessUserId,
    metadata?.merchantUserId,
    metadata?.vendorUserId,
    metadata?.createdBy,
    metadata?.createdByUserId,
    metadata?.senderUserId,
    metadata?.requestedBy,
    metadata?.requesterUserId,
    metadata?.warehouseOwnerUserId,
    metadata?.sourceOwnerUserId,
    warehouse?.ownerUserId,
    warehouse?.ownerId,
    warehouse?.accountUserId,
    warehouse?.businessUserId,
    warehouse?.createdBy,
  ].forEach(add);

  [
    metadata?.accountUserIds,
    metadata?.ownerUserIds,
    metadata?.sellerUserIds,
  ].forEach(addMany);

  /*
   * Legacy fallback only. Generic userId can represent different things on
   * newer records, so it is used only when no stronger account field exists.
   */
  if (ids.size === 0) {
    add(record?.userId);
    add(metadata?.userId);
  }

  return ids;
};

const recordMatchesAccount = (record: any, accountUserId: string) => {
  const accountId = clean(accountUserId);
  if (!accountId) return true;

  return getRecordAccountIds(record).has(accountId);
};

const getAccountId = (record: any) =>
  clean(
    record?.uid ||
      record?.firebaseUid ||
      record?.userId ||
      record?.id ||
      record?._id,
  );

const getAccountLabel = (record: any, id: string) =>
  clean(
    record?.businessName ||
      record?.companyName ||
      record?.displayName ||
      record?.name ||
      record?.email ||
      id,
  );

const DriverAssignmentPageContent: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    userId,
    role,
    isSuperAdmin,
    loading: accessLoading,
  } = useAdminAccess();

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accountRows, setAccountRows] = useState<any[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const {
    data: jobs = [],
    isLoading: isJobsLoading,
    refetch: refetchJobs,
  } = useGetDispatchJobsForPinManagementQuery();

  const {
    data: drivers = [],
    isLoading: isDriversLoading,
    refetch: refetchDrivers,
  } = useGetDriversQuery();

  const [claimDispatchJob, { isLoading: isClaiming }] =
    useClaimDispatchJobMutation();

  const [updateJobStatus, { isLoading: isUpdatingStatus }] =
    useUpdateDispatchJobStatusMutation();

  const [verifyDispatchJobDeliveryPin, { isLoading: isVerifyingPin }] =
    useVerifyDispatchJobDeliveryPinMutation();

  const [deleteDispatchJob, { isLoading: isDeletingJob }] =
    useDeleteDispatchJobMutation();

  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [jobSearch, setJobSearch] = useState("");

  const [selectedJob, setSelectedJob] = useState<DispatchJob | null>(null);
  const [modalDriverId, setModalDriverId] = useState("");
  const [nextStatus, setNextStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [pin, setPin] = useState("");
  const [cashCollected, setCashCollected] = useState(false);
  const [cashCollectedAmount, setCashCollectedAmount] = useState("");
  const [overrideTransition, setOverrideTransition] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [nearbyRadiusKm, setNearbyRadiusKm] = useState(25);
  const [nearbyDriversLoading, setNearbyDriversLoading] = useState(false);
  const [nearbyDriversResolved, setNearbyDriversResolved] = useState(false);
  const [nearbyDriversError, setNearbyDriversError] = useState("");
  const [isUnassigningDriver, setIsUnassigningDriver] = useState(false);

  useEffect(() => {
    if (accessLoading || !userId) return;

    if (!isSuperAdmin) {
      setSelectedAccountId(userId);
    }
  }, [accessLoading, isSuperAdmin, userId]);

  useEffect(() => {
    if (accessLoading || !isSuperAdmin) {
      setAccountRows([]);
      return;
    }

    let active = true;

    const loadAccounts = async () => {
      try {
        setAccountsLoading(true);

        const results = await Promise.allSettled([
          axios.get(`${API_BASE}/api/v1/admin/users`, {
            params: { page: 1, limit: 500 },
          }),
          axios.get(`${API_BASE}/api/v1/users`, {
            params: { page: 1, limit: 500 },
          }),
        ]);

        const firstSuccessful = results.find(
          (result) =>
            result.status === "fulfilled" &&
            toArray<any>(result.value.data).length > 0,
        );

        if (!active) return;

        setAccountRows(
          firstSuccessful?.status === "fulfilled"
            ? toArray<any>(firstSuccessful.value.data)
            : [],
        );
      } catch (error) {
        console.warn("Failed to load account dropdown:", error);
        if (active) setAccountRows([]);
      } finally {
        if (active) setAccountsLoading(false);
      }
    };

    void loadAccounts();

    return () => {
      active = false;
    };
  }, [accessLoading, isSuperAdmin]);

  const accountOptions = useMemo<AccountOption[]>(() => {
    const map = new Map<string, AccountOption>();

    const addOption = (record: any) => {
      const id = getAccountId(record);
      if (!id) return;

      map.set(id, {
        id,
        label: getAccountLabel(record, id),
        email: clean(record?.email) || undefined,
      });
    };

    accountRows.forEach(addOption);

    [...jobs, ...drivers].forEach((record: any) => {
      getRecordAccountIds(record).forEach((id) => {
        if (!map.has(id)) {
          map.set(id, {
            id,
            label: id,
          });
        }
      });
    });

    if (userId && !map.has(userId)) {
      map.set(userId, {
        id: userId,
        label: userId,
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [accountRows, drivers, jobs, userId]);

  const activeAccountId = isSuperAdmin ? selectedAccountId : userId;

  const activeAccountLabel = useMemo(() => {
    if (isSuperAdmin && !activeAccountId) {
      return "All accounts";
    }

    return (
      accountOptions.find((account) => account.id === activeAccountId)?.label ||
      activeAccountId ||
      "Current account"
    );
  }, [accountOptions, activeAccountId, isSuperAdmin]);

  const scopedJobs = useMemo(
    () =>
      jobs.filter((job: DispatchJob) =>
        recordMatchesAccount(job, activeAccountId),
      ),
    [activeAccountId, jobs],
  );

  const scopedDrivers = useMemo(
    () =>
      drivers.filter((driver: DriverRow) =>
        recordMatchesAccount(driver, activeAccountId),
      ),
    [activeAccountId, drivers],
  );

  const canManageJob = (job: DispatchJob | null) => {
    if (!job || !userId) return false;

    if (isSuperAdmin && !activeAccountId) {
      return true;
    }

    return recordMatchesAccount(job, activeAccountId);
  };

  const allDriverIds = useMemo(() => {
    const ids = Array.from(
      new Set(
        scopedJobs
          .map((job: DispatchJob) => String(job.assignedDriverId || "").trim())
          .filter(Boolean),
      ),
    );
    return ids;
  }, [scopedJobs]);

  const filteredJobs = useMemo(() => {
    return scopedJobs.filter((job: DispatchJob) => {
      const matchesDriver = selectedDriverId
        ? String(job.assignedDriverId || "") === String(selectedDriverId)
        : true;

      const query = jobSearch.trim().toLowerCase();
      const haystack = [
        job._id,
        job.customerName,
        job.customerPhone,
        job.pickupAddress,
        job.dropoffAddress,
        job.status,
        job.sourceType,
        job.sourceId,
        job.assignedDriverId,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");

      const matchesSearch = query ? haystack.includes(query) : true;

      return matchesDriver && matchesSearch;
    });
  }, [scopedJobs, selectedDriverId, jobSearch]);

  const activeDrivers = useMemo(() => {
    return scopedDrivers.filter((driver: DriverRow) => {
      const status = String(driver.status || "").toLowerCase();
      const shift = String(driver.currentShiftStatus || "").toLowerCase();

      return status === "active" && shift === "on_shift";
    });
  }, [scopedDrivers]);

  const availableStatuses = useMemo(() => {
    if (overrideTransition) {
      return ALL_STATUSES;
    }

    const currentStatus = normalizeStatus(selectedJob?.status);
    return STATUS_TRANSITIONS[currentStatus] || [];
  }, [selectedJob?.status, overrideTransition]);

  const canAssignDriver = useMemo(() => {
    if (!selectedJob) return false;
    return (
      !selectedJob.assignedDriverId &&
      CLAIMABLE_STATUSES.includes(normalizeStatus(selectedJob.status))
    );
  }, [selectedJob]);

  const canUnassignDriver = useMemo(() => {
    if (!selectedJob?.assignedDriverId) return false;

    return UNASSIGNABLE_DRIVER_STATUSES.has(
      normalizeStatus(selectedJob.status),
    );
  }, [selectedJob]);

  const selectedJobHasPickupCoordinates = Boolean(
    selectedJob &&
    Number.isFinite(Number((selectedJob as any)?.pickupLatLng?.lat)) &&
    Number.isFinite(Number((selectedJob as any)?.pickupLatLng?.lng)),
  );

  const candidateDriverRows = useMemo(() => {
    if (!selectedJob) return [] as any[];

    const details = Array.isArray((selectedJob as any)?.candidateDriverDetails)
      ? (selectedJob as any).candidateDriverDetails
      : [];
    const candidateIds = Array.isArray((selectedJob as any)?.candidateDriverIds)
      ? (selectedJob as any).candidateDriverIds.map((value: unknown) =>
          clean(value),
        )
      : [];

    const detailsById = new Map(
      details
        .filter((row: any) => clean(row?.driverId))
        .map((row: any) => [clean(row.driverId), row]),
    );
    const driverById = new Map(
      activeDrivers.map((driver: DriverRow) => [clean(driver.id), driver]),
    );

    return candidateIds
      .map((id: string, index: number) => {
        const detail: any = detailsById.get(id) || {};
        const driver: any = driverById.get(id) || {};
        return {
          ...driver,
          ...detail,
          id,
          driverId: id,
          rank: Number(detail?.rank || index + 1),
          name: detail?.name || driver?.name || id,
          vehicleType: detail?.vehicleType || driver?.vehicleType || "other",
          currentJobsCount: Number(
            detail?.currentJobsCount ?? driver?.currentJobsCount ?? 0,
          ),
          maxConcurrentJobs: Number(
            detail?.maxConcurrentJobs ?? driver?.maxConcurrentJobs ?? 2,
          ),
          ratingScore: Number(detail?.ratingScore ?? driver?.ratingScore ?? 0),
        };
      })
      .sort((a: any, b: any) => Number(a.rank || 999) - Number(b.rank || 999));
  }, [activeDrivers, selectedJob]);

  const refreshNearbyCandidates = async (
    job: DispatchJob,
    radiusKm: number,
  ) => {
    if (!job?._id) return;

    const pickup = (job as any)?.pickupLatLng;
    const hasPickup =
      Number.isFinite(Number(pickup?.lat)) &&
      Number.isFinite(Number(pickup?.lng));

    if (!hasPickup) {
      setNearbyDriversResolved(false);
      setNearbyDriversError(
        "This legacy dispatch job has no pickup coordinates. The existing active-driver selector remains available.",
      );
      return;
    }

    try {
      setNearbyDriversLoading(true);
      setNearbyDriversError("");

      const response = await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${job._id}/refresh-candidates`,
        {
          radiusKm,
          expandRadius: false,
        },
      );

      const refreshed = response.data?.data || response.data;
      if (!refreshed?._id) {
        throw new Error(
          "The server did not return the refreshed dispatch job.",
        );
      }

      setSelectedJob(refreshed as DispatchJob);
      setNearbyDriversResolved(true);

      const candidateIds = Array.isArray(refreshed?.candidateDriverIds)
        ? refreshed.candidateDriverIds
        : [];

      if (!candidateIds.length) {
        setModalDriverId("");
        setNearbyDriversError(
          `No eligible warehouse or nearby driver is currently available. Warehouse drivers are never excluded by distance; increase the radius only to add more nearby drivers.`,
        );
      } else if (
        modalDriverId &&
        !candidateIds
          .map((value: unknown) => clean(value))
          .includes(modalDriverId)
      ) {
        setModalDriverId("");
      }
    } catch (error: any) {
      console.error("Failed to find nearby drivers for dispatch", error);
      setNearbyDriversResolved(false);
      setNearbyDriversError(
        error?.response?.data?.message ||
          error?.message ||
          "Could not refresh nearby drivers.",
      );
    } finally {
      setNearbyDriversLoading(false);
    }
  };

  const openManageModal = (job: DispatchJob) => {
    if (!canManageJob(job)) {
      toast({
        status: "error",
        title: "Access denied",
        description:
          "You can manage only dispatch jobs linked to the selected account.",
      });
      return;
    }

    setSelectedJob(job);
    setModalDriverId("");
    setNextStatus("");
    setNotes("");
    setFailureReason("");
    setReturnReason("");
    setPin("");
    setCashCollected(false);
    setCashCollectedAmount("");
    setOverrideTransition(false);
    setConfirmDeleteText("");
    setNearbyRadiusKm(25);
    setNearbyDriversResolved(false);
    setNearbyDriversError("");
    onOpen();

    const pickup = (job as any)?.pickupLatLng;
    if (
      Number.isFinite(Number(pickup?.lat)) &&
      Number.isFinite(Number(pickup?.lng))
    ) {
      void refreshNearbyCandidates(job, 25);
    }
  };

  const closeManageModal = () => {
    if (
      isClaiming ||
      isUpdatingStatus ||
      isVerifyingPin ||
      isDeletingJob ||
      isUnassigningDriver
    )
      return;

    setSelectedJob(null);
    setModalDriverId("");
    setNextStatus("");
    setNotes("");
    setFailureReason("");
    setReturnReason("");
    setPin("");
    setCashCollected(false);
    setCashCollectedAmount("");
    setOverrideTransition(false);
    setConfirmDeleteText("");
    setNearbyRadiusKm(25);
    setNearbyDriversResolved(false);
    setNearbyDriversError("");
    onClose();
  };

  const handleUnassignDriver = async () => {
    if (!selectedJob?._id || !selectedJob.assignedDriverId) return;

    if (!canManageJob(selectedJob)) {
      toast({
        status: "error",
        title: "Access denied",
        description:
          "This dispatch job is not linked to the selected profile/account.",
      });
      return;
    }

    if (!canUnassignDriver) {
      toast({
        status: "warning",
        title: "Driver cannot be unassigned",
        description:
          "The parcel has already been picked up or delivery movement has progressed. Use the existing cancel/return/status workflow instead.",
        duration: 4500,
        isClosable: true,
      });
      return;
    }

    const driverId = String(selectedJob.assignedDriverId || "").trim();
    const confirmed = window.confirm(
      `Unassign driver ${driverId} from this dispatch job?\n\nThe dispatch job will return to Pending Dispatch so you can choose another driver. The order is not deleted or cancelled.`,
    );

    if (!confirmed) return;

    try {
      setIsUnassigningDriver(true);

      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error("Please sign in again before unassigning a driver.");
      }

      const idToken = await currentUser.getIdToken();
      const response = await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${selectedJob._id}/unassign`,
        {
          reason: "admin_unassigned",
          refreshCandidates: true,
          notifyDrivers: false,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      const result = response.data?.data || response.data;
      if (result?.unassigned === false && result?.reason === "not_assigned") {
        toast({
          status: "info",
          title: "Driver already unassigned",
        });
      } else {
        toast({
          status: "success",
          title: "Driver unassigned",
          description:
            "The job is back in Pending Dispatch. Warehouse and nearby candidates have been refreshed so you can assign a replacement.",
          duration: 3500,
          isClosable: true,
        });
      }

      const refreshedJob = result?.job;
      if (refreshedJob?._id) {
        setSelectedJob(refreshedJob as DispatchJob);
        setNearbyDriversResolved(
          Array.isArray(refreshedJob?.candidateDriverIds),
        );
      } else {
        setSelectedJob((current) =>
          current
            ? ({
                ...current,
                status: "pending_dispatch",
                assignedDriverId: undefined,
              } as DispatchJob)
            : current,
        );
        setNearbyDriversResolved(false);
      }

      setModalDriverId("");
      setNextStatus("");
      setNearbyDriversError("");

      await Promise.allSettled([refetchJobs(), refetchDrivers()]);
    } catch (error: any) {
      toast({
        status: "error",
        title: "Failed to unassign driver",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "The driver could not be unassigned.",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUnassigningDriver(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedJob?._id) {
      toast({
        status: "error",
        title: "No job selected",
      });
      return;
    }

    if (!canManageJob(selectedJob)) {
      toast({
        status: "error",
        title: "Access denied",
        description:
          "This dispatch job is not linked to the selected profile/account.",
      });
      return;
    }

    const trimmedStatus = String(nextStatus || "").trim();
    const trimmedDriverId = String(modalDriverId || "").trim();
    const amountNumber = Number(cashCollectedAmount || 0);

    if (canAssignDriver && trimmedDriverId) {
      try {
        const claimResult = await claimDispatchJob({
          jobId: selectedJob._id,
          driverId: trimmedDriverId,
        }).unwrap();

        if (claimResult?.assigned === false) {
          toast({
            status: "warning",
            title: "Job already taken",
            description: "This job was already assigned.",
          });
        } else {
          toast({
            status: "success",
            title: "Driver assigned",
          });
        }
      } catch (error: any) {
        toast({
          status: "error",
          title: "Failed to assign driver",
          description:
            error?.data?.message || error?.message || "Unable to assign driver",
        });
        return;
      }
    }

    if (!trimmedStatus) {
      if (canAssignDriver && trimmedDriverId) {
        closeManageModal();
        return;
      }

      toast({
        status: "warning",
        title: "Please select the next status",
      });
      return;
    }

    try {
      if (
        !overrideTransition &&
        normalizeStatus(selectedJob.status) === "proof_of_delivery_pending" &&
        trimmedStatus === "delivered"
      ) {
        if (!pin.trim()) {
          toast({
            status: "warning",
            title: "PIN required",
            description: "Enter the delivery PIN to complete delivery.",
          });
          return;
        }

        await verifyDispatchJobDeliveryPin({
          jobId: selectedJob._id,
          pin: pin.trim(),
          driverId: selectedJob.assignedDriverId || undefined,
          cashCollected,
          cashCollectedAmount: amountNumber,
        }).unwrap();
      } else {
        await updateJobStatus({
          jobId: selectedJob._id,
          status: trimmedStatus,
          cashCollected,
          cashCollectedAmount: amountNumber,
          failureReason: failureReason || undefined,
          returnReason: returnReason || undefined,
          notes: notes || undefined,
          overrideTransition,
        }).unwrap();
      }

      toast({
        status: "success",
        title: "Job updated",
        description: `${overrideTransition ? "Override applied: " : "Updated to "}${prettifyStatus(trimmedStatus)}`,
      });

      closeManageModal();
    } catch (error: any) {
      toast({
        status: "error",
        title: "Failed to update job",
        description:
          error?.data?.message ||
          error?.message ||
          "Some updates could not be completed.",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteJob = async () => {
    if (!selectedJob?._id) {
      return;
    }

    if (!canManageJob(selectedJob)) {
      toast({
        status: "error",
        title: "Access denied",
        description:
          "This dispatch job is not linked to the selected profile/account.",
      });
      return;
    }

    if (confirmDeleteText.trim().toUpperCase() !== "DELETE") {
      toast({
        status: "warning",
        title: "Type DELETE to confirm",
      });
      return;
    }

    try {
      await deleteDispatchJob({ jobId: selectedJob._id }).unwrap();

      toast({
        status: "success",
        title: "Dispatch job deleted",
      });

      closeManageModal();
    } catch (error: any) {
      toast({
        status: "error",
        title: "Failed to delete job",
        description:
          error?.data?.message ||
          error?.message ||
          "This job could not be deleted.",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (accessLoading) {
    return (
      <Flex
        minH="240px"
        justify="center"
        align="center"
        direction="column"
        gap={3}
      >
        <Spinner size="lg" />
        <Text color="gray.500">Checking account access...</Text>
      </Flex>
    );
  }

  if (!userId) {
    return (
      <Flex
        minH="240px"
        justify="center"
        align="center"
        direction="column"
        gap={3}
      >
        <Text fontWeight="700">Sign in required</Text>
        <Text color="gray.500">
          Sign in before opening driver jobs and assignments.
        </Text>
      </Flex>
    );
  }

  return (
    <Box p={0}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="md" mb={1}>
            Driver Jobs & Assignments
          </Heading>
          <Text color="gray.500" fontSize="sm">
            View and manage dispatch jobs with responsive scrolling, override
            support, and delete controls.
          </Text>

          <HStack mt={3} spacing={2} flexWrap="wrap">
            <Badge
              borderRadius="full"
              px={3}
              py={1}
              colorScheme={isSuperAdmin ? "purple" : "blue"}
            >
              {isSuperAdmin
                ? activeAccountId
                  ? `Account: ${activeAccountLabel}`
                  : "All platform accounts"
                : "Current account only"}
            </Badge>

            <Text fontSize="xs" color="gray.500">
              Role: {role || "user"} · UID: {userId}
            </Text>
          </HStack>
        </Box>

        <Box
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="2xl"
          p={{ base: 4, md: 5 }}
          boxShadow="sm"
        >
          <Flex
            direction={{ base: "column", lg: "row" }}
            gap={4}
            justify="space-between"
            align={{ base: "stretch", lg: "center" }}
          >
            <Box flex="1">
              <Text mb={2} fontWeight="semibold" fontSize="sm">
                Profile / Account
              </Text>
              <Select
                value={activeAccountId}
                onChange={(event) => {
                  if (isSuperAdmin) {
                    setSelectedAccountId(event.target.value);
                    setSelectedDriverId("");
                  }
                }}
                isDisabled={!isSuperAdmin || accountsLoading}
                borderRadius="xl"
                h="48px"
              >
                {isSuperAdmin && <option value="">All accounts</option>}

                {accountOptions.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.label}
                    {account.email && account.email !== account.label
                      ? ` • ${account.email}`
                      : ""}
                  </option>
                ))}
              </Select>
              {!isSuperAdmin && (
                <Text mt={1} fontSize="xs" color="gray.500">
                  Your signed-in profile/account is selected automatically.
                </Text>
              )}
            </Box>

            <Box flex="1">
              <Text mb={2} fontWeight="semibold" fontSize="sm">
                Filter by driver
              </Text>
              <Select
                placeholder="All drivers"
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                borderRadius="xl"
                h="48px"
              >
                {allDriverIds.map((driverId) => (
                  <option key={driverId} value={driverId}>
                    {driverId}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex="1.2">
              <Text mb={2} fontWeight="semibold" fontSize="sm">
                Search jobs
              </Text>
              <Input
                placeholder="Search by job, customer, phone, address..."
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                borderRadius="xl"
                h="48px"
              />
            </Box>
          </Flex>
        </Box>

        <Box
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="sm"
        >
          <Flex
            px={{ base: 4, md: 6 }}
            py={4}
            align="center"
            justify="space-between"
            borderBottomWidth="1px"
            borderColor="gray.100"
            bg="gray.50"
          >
            <Box>
              <Text fontWeight="bold" fontSize="md">
                Assigned Jobs
              </Text>
              <Text fontSize="sm" color="gray.500">
                {filteredJobs.length} job{filteredJobs.length === 1 ? "" : "s"}{" "}
                found
              </Text>
            </Box>
          </Flex>

          {isJobsLoading ? (
            <Flex
              py={12}
              justify="center"
              align="center"
              direction="column"
              gap={3}
            >
              <Spinner size="lg" />
              <Text color="gray.500">Loading jobs...</Text>
            </Flex>
          ) : filteredJobs.length === 0 ? (
            <Flex
              py={12}
              justify="center"
              align="center"
              direction="column"
              gap={3}
            >
              <Text fontSize="lg" fontWeight="semibold">
                No jobs found
              </Text>
              <Text color="gray.500" textAlign="center">
                No jobs matched the selected driver or search term.
              </Text>
            </Flex>
          ) : (
            <>
              <VStack
                display={{ base: "flex", md: "none" }}
                align="stretch"
                spacing={3}
                p={3}
                maxH="68vh"
                overflowY="auto"
              >
                {filteredJobs.map((job: DispatchJob) => (
                  <Box
                    key={job._id}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={4}
                    bg="white"
                  >
                    <Flex
                      justify="space-between"
                      align="flex-start"
                      gap={3}
                      mb={3}
                    >
                      <Box minW={0}>
                        <Text
                          fontWeight="bold"
                          fontSize="sm"
                          wordBreak="break-all"
                        >
                          {job._id}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Dispatch job
                        </Text>
                      </Box>
                      <Badge
                        flexShrink={0}
                        colorScheme={getStatusColorScheme(job.status)}
                        borderRadius="full"
                        px={3}
                        py={1}
                        whiteSpace="nowrap"
                      >
                        {prettifyStatus(job.status)}
                      </Badge>
                    </Flex>

                    <SimpleGrid columns={2} spacing={3} mb={3}>
                      <Box minW={0}>
                        <Text fontSize="xs" color="gray.500">
                          Driver
                        </Text>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                          {job.assignedDriverId || "-"}
                        </Text>
                      </Box>
                      <Box minW={0}>
                        <Text fontSize="xs" color="gray.500">
                          Customer
                        </Text>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                          {job.customerName || "-"}
                        </Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {job.customerPhone || "-"}
                        </Text>
                      </Box>
                      <Box minW={0}>
                        <Text fontSize="xs" color="gray.500">
                          Source
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          textTransform="capitalize"
                          noOfLines={1}
                        >
                          {job.sourceType || "-"}
                        </Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {job.sourceId || "-"}
                        </Text>
                      </Box>
                      <Box minW={0}>
                        <Text fontSize="xs" color="gray.500">
                          Order value
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold">
                          {formatMoney(job.orderValue)}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Box mb={3}>
                      <Text fontSize="xs" color="gray.500">
                        Route
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {job.pickupAddress || "-"}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        to {job.dropoffAddress || "-"}
                      </Text>
                    </Box>

                    <Flex justify="space-between" align="center" gap={3} mb={4}>
                      <Text fontSize="xs" color="gray.500">
                        Driver earning
                      </Text>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="green.600"
                      >
                        {formatMoney(job.driverEarning)}
                      </Text>
                    </Flex>

                    <Button
                      w="100%"
                      size="sm"
                      borderRadius="full"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => openManageModal(job)}
                    >
                      Manage
                    </Button>
                  </Box>
                ))}
              </VStack>

              <Box
                display={{ base: "none", md: "block" }}
                overflowX="auto"
                maxH={{ md: "65vh", xl: "70vh" }}
                overflowY="auto"
                sx={{ WebkitOverflowScrolling: "touch" }}
              >
                <Table variant="simple" minW="1220px" size="sm">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th py={3}>Job</Th>
                      <Th py={3}>Driver</Th>
                      <Th py={3}>Customer</Th>
                      <Th py={3}>Source</Th>
                      <Th py={3}>Status</Th>
                      <Th py={3}>Route</Th>
                      <Th py={3} isNumeric>
                        Order Value
                      </Th>
                      <Th py={3} isNumeric>
                        Driver Earning
                      </Th>
                      <Th py={3}>Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredJobs.map((job: DispatchJob) => (
                      <Tr key={job._id} _hover={{ bg: "gray.50" }}>
                        <Td py={3}>
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="semibold"
                              fontSize="sm"
                              noOfLines={1}
                            >
                              {job._id}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Job ID
                            </Text>
                          </VStack>
                        </Td>

                        <Td py={3}>
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="medium"
                              fontSize="sm"
                              noOfLines={1}
                            >
                              {job.assignedDriverId || "-"}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Assigned driver
                            </Text>
                          </VStack>
                        </Td>

                        <Td py={3}>
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="medium"
                              fontSize="sm"
                              noOfLines={1}
                            >
                              {job.customerName || "-"}
                            </Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              {job.customerPhone || "-"}
                            </Text>
                          </VStack>
                        </Td>

                        <Td py={3}>
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="medium"
                              fontSize="sm"
                              textTransform="capitalize"
                              noOfLines={1}
                            >
                              {job.sourceType || "-"}
                            </Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              {job.sourceId || "-"}
                            </Text>
                          </VStack>
                        </Td>

                        <Td py={3}>
                          <Badge
                            colorScheme={getStatusColorScheme(job.status)}
                            borderRadius="full"
                            px={3}
                            py={1}
                            whiteSpace="nowrap"
                          >
                            {prettifyStatus(job.status)}
                          </Badge>
                        </Td>

                        <Td py={3} maxW="320px">
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              noOfLines={1}
                            >
                              {job.pickupAddress || "-"}
                            </Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              to {job.dropoffAddress || "-"}
                            </Text>
                          </Box>
                        </Td>

                        <Td py={3} isNumeric whiteSpace="nowrap">
                          <Text fontWeight="semibold" fontSize="sm">
                            {formatMoney(job.orderValue)}
                          </Text>
                        </Td>

                        <Td py={3} isNumeric whiteSpace="nowrap">
                          <Text
                            fontWeight="semibold"
                            fontSize="sm"
                            color="green.600"
                          >
                            {formatMoney(job.driverEarning)}
                          </Text>
                        </Td>

                        <Td py={3}>
                          <Button
                            size="sm"
                            borderRadius="full"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => openManageModal(job)}
                          >
                            Manage
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </>
          )}
        </Box>
      </VStack>

      <Modal
        isOpen={isOpen}
        onClose={closeManageModal}
        isCentered
        size={{ base: "full", md: "2xl" }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          borderRadius={{ base: 0, md: "2xl" }}
          maxH={{ base: "100vh", md: "90vh" }}
        >
          <ModalHeader>Manage Dispatch Job</ModalHeader>
          <ModalCloseButton />

          <ModalBody overflowY="auto">
            <VStack spacing={5} align="stretch">
              <Box
                bg="gray.50"
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="xl"
                p={4}
              >
                <VStack align="stretch" spacing={2}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Job ID
                    </Text>
                    <Text fontWeight="bold" fontSize="sm" wordBreak="break-all">
                      {selectedJob?._id || "-"}
                    </Text>
                  </Box>

                  <HStack align="start" spacing={6} flexWrap="wrap">
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Current Driver
                      </Text>
                      <Text fontWeight="medium" fontSize="sm">
                        {selectedJob?.assignedDriverId || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Current Status
                      </Text>
                      <Badge
                        mt={1}
                        colorScheme={getStatusColorScheme(selectedJob?.status)}
                        borderRadius="full"
                        px={3}
                        py={1}
                      >
                        {prettifyStatus(selectedJob?.status)}
                      </Badge>
                    </Box>
                  </HStack>
                </VStack>
              </Box>

              {selectedJob?.assignedDriverId && (
                <Flex
                  p={4}
                  borderWidth="1px"
                  borderColor={canUnassignDriver ? "red.200" : "gray.200"}
                  borderRadius="xl"
                  bg={canUnassignDriver ? "red.50" : "gray.50"}
                  justify="space-between"
                  align={{ base: "stretch", sm: "center" }}
                  direction={{ base: "column", sm: "row" }}
                  gap={3}
                >
                  <Box>
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      color={canUnassignDriver ? "red.700" : "gray.700"}
                    >
                      Assigned driver
                    </Text>
                    <Text
                      mt={1}
                      fontSize="xs"
                      color={canUnassignDriver ? "red.600" : "gray.600"}
                    >
                      {canUnassignDriver
                        ? "Unassigning keeps the job and order intact, releases the driver's job slot, and returns this job to Pending Dispatch for reassignment."
                        : "This driver cannot be unassigned because parcel pickup/delivery movement has already progressed. Use the normal cancel, return, or status workflow."}
                    </Text>
                  </Box>

                  <Button
                    flexShrink={0}
                    colorScheme="red"
                    variant="outline"
                    borderRadius="full"
                    onClick={handleUnassignDriver}
                    isLoading={isUnassigningDriver}
                    loadingText="Unassigning"
                    isDisabled={
                      !canUnassignDriver ||
                      isClaiming ||
                      isUpdatingStatus ||
                      isVerifyingPin ||
                      isDeletingJob
                    }
                  >
                    Unassign Driver
                  </Button>
                </Flex>
              )}

              <Flex
                p={4}
                borderWidth="1px"
                borderColor="orange.200"
                borderRadius="xl"
                bg="orange.50"
                justify="space-between"
                align="center"
                gap={4}
                wrap="wrap"
              >
                <Box>
                  <Text fontWeight="semibold" fontSize="sm">
                    Override transition rules
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Use this only for admin correction when the normal
                    transition flow is blocked.
                  </Text>
                </Box>
                <Switch
                  colorScheme="orange"
                  isChecked={overrideTransition}
                  onChange={(e) => {
                    setOverrideTransition(e.target.checked);
                    setNextStatus("");
                  }}
                />
              </Flex>

              {canAssignDriver && (
                <FormControl>
                  <FormLabel>Assign driver</FormLabel>

                  {selectedJobHasPickupCoordinates ? (
                    <VStack align="stretch" spacing={3}>
                      <Flex
                        direction={{ base: "column", sm: "row" }}
                        gap={2}
                        align={{ base: "stretch", sm: "end" }}
                      >
                        <Box flex="1">
                          <Text mb={1} fontSize="xs" color="gray.500">
                            Search radius from pickup
                          </Text>
                          <Select
                            value={String(nearbyRadiusKm)}
                            onChange={(e) =>
                              setNearbyRadiusKm(Number(e.target.value))
                            }
                            borderRadius="xl"
                          >
                            {[5, 10, 25, 50, 100].map((radius) => (
                              <option key={radius} value={radius}>
                                {radius} km
                              </option>
                            ))}
                          </Select>
                        </Box>
                        <Button
                          onClick={() =>
                            selectedJob &&
                            void refreshNearbyCandidates(
                              selectedJob,
                              nearbyRadiusKm,
                            )
                          }
                          isLoading={nearbyDriversLoading}
                          loadingText="Finding"
                          variant="outline"
                          borderRadius="xl"
                        >
                          Find nearby drivers
                        </Button>
                      </Flex>

                      {nearbyDriversLoading ? (
                        <Flex align="center" gap={2} py={2}>
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.600">
                            Ranking eligible drivers by live distance to pickup…
                          </Text>
                        </Flex>
                      ) : null}

                      {nearbyDriversError ? (
                        <Box
                          border="1px solid"
                          borderColor={
                            candidateDriverRows.length
                              ? "orange.200"
                              : "red.200"
                          }
                          bg={
                            candidateDriverRows.length ? "orange.50" : "red.50"
                          }
                          color={
                            candidateDriverRows.length
                              ? "orange.700"
                              : "red.700"
                          }
                          borderRadius="xl"
                          p={3}
                        >
                          <Text fontSize="sm">{nearbyDriversError}</Text>
                        </Box>
                      ) : null}

                      {nearbyDriversResolved &&
                      candidateDriverRows.length > 0 ? (
                        <VStack align="stretch" spacing={2}>
                          {Boolean(
                            (selectedJob as any)?.metadata
                              ?.driverLocationFallbackUsed,
                          ) ? (
                            <Text fontSize="xs" color="orange.600">
                              No eligible driver currently has fresh GPS. These
                              are the existing eligible drivers; distance will
                              appear as soon as they share location.
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="gray.500">
                              Hybrid mode keeps every eligible warehouse driver
                              in the candidate pool and adds live nearby
                              drivers. GPS distance only improves ranking;
                              assignment still uses the existing atomic claim
                              protection.
                            </Text>
                          )}

                          {candidateDriverRows.map(
                            (driver: any, index: number) => {
                              const id = clean(driver.driverId || driver.id);
                              const selected = modalDriverId === id;
                              const distance = Number(
                                driver.distanceToPickupKm,
                              );
                              const hasDistance = Number.isFinite(distance);

                              return (
                                <Box
                                  key={id}
                                  p={3}
                                  border="1px solid"
                                  borderColor={
                                    selected ? "blue.400" : "gray.200"
                                  }
                                  bg={selected ? "blue.50" : "white"}
                                  borderRadius="xl"
                                  cursor="pointer"
                                  onClick={() => setModalDriverId(id)}
                                  _hover={{ borderColor: "blue.300" }}
                                >
                                  <Flex
                                    justify="space-between"
                                    align="start"
                                    gap={3}
                                  >
                                    <HStack align="start" spacing={3}>
                                      <VStack spacing={1} align="start">
                                        <Badge
                                          colorScheme={
                                            index === 0 ? "green" : "gray"
                                          }
                                          borderRadius="full"
                                        >
                                          #{index + 1}
                                        </Badge>
                                        <Badge
                                          colorScheme={
                                            driver.candidateSource ===
                                            "warehouse_nearby"
                                              ? "purple"
                                              : driver.candidateSource ===
                                                  "warehouse"
                                                ? "blue"
                                                : "green"
                                          }
                                          borderRadius="full"
                                        >
                                          {driver.candidateSource ===
                                          "warehouse_nearby"
                                            ? "Warehouse + Nearby"
                                            : driver.candidateSource ===
                                                "warehouse"
                                              ? "Warehouse"
                                              : "Nearby"}
                                        </Badge>
                                      </VStack>
                                      <Box>
                                        <Text fontWeight="700" fontSize="sm">
                                          {driver.name || id}
                                        </Text>
                                        <Text fontSize="xs" color="gray.600">
                                          {driver.vehicleType || "vehicle"}
                                          {hasDistance
                                            ? ` • ${distance.toFixed(1)} km • ~${driver.estimatedPickupMinutes || "-"} min`
                                            : " • GPS distance unavailable"}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          {Number(driver.currentJobsCount || 0)}
                                          /
                                          {Number(
                                            driver.maxConcurrentJobs || 2,
                                          )}{" "}
                                          jobs
                                          {Number(driver.ratingScore || 0) > 0
                                            ? ` • ⭐ ${Number(driver.ratingScore).toFixed(1)}`
                                            : ""}
                                        </Text>
                                      </Box>
                                    </HStack>
                                    <Badge
                                      colorScheme={
                                        driver.locationFresh
                                          ? "green"
                                          : "orange"
                                      }
                                    >
                                      {driver.locationFresh
                                        ? "Recent GPS"
                                        : "Eligible"}
                                    </Badge>
                                  </Flex>
                                </Box>
                              );
                            },
                          )}
                        </VStack>
                      ) : nearbyDriversResolved && !nearbyDriversLoading ? (
                        <Text fontSize="sm" color="gray.600">
                          No eligible driver is currently inside this radius.
                        </Text>
                      ) : null}
                    </VStack>
                  ) : (
                    <>
                      <Select
                        placeholder={
                          isDriversLoading
                            ? "Loading drivers..."
                            : "Select active driver"
                        }
                        value={modalDriverId}
                        onChange={(e) => setModalDriverId(e.target.value)}
                        borderRadius="xl"
                      >
                        {activeDrivers.map((driver) => (
                          <option key={driver.id} value={driver.id}>
                            {driver.name || driver.id}
                            {driver.vehicleType
                              ? ` • ${driver.vehicleType}`
                              : ""}
                            {typeof driver.currentJobsCount === "number"
                              ? ` • Jobs: ${driver.currentJobsCount}`
                              : ""}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText>
                        This legacy job has no pickup coordinates, so the
                        existing active-driver assignment flow is preserved.
                      </FormHelperText>
                    </>
                  )}
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Next status</FormLabel>
                <Select
                  placeholder={
                    availableStatuses.length
                      ? "Select next status"
                      : "No valid next status available"
                  }
                  value={nextStatus}
                  onChange={(e) => setNextStatus(e.target.value)}
                  borderRadius="xl"
                  isDisabled={availableStatuses.length === 0}
                >
                  {availableStatuses.map((status) => (
                    <option key={status} value={status}>
                      {prettifyStatus(status)}
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  {overrideTransition
                    ? "Override mode shows all statuses."
                    : "Only backend-supported next steps are shown."}
                </FormHelperText>
              </FormControl>

              {nextStatus === "delivery_failed_customer_unavailable" && (
                <FormControl>
                  <FormLabel>Failure reason</FormLabel>
                  <Input
                    placeholder="customer_not_available"
                    value={failureReason}
                    onChange={(e) => setFailureReason(e.target.value)}
                    borderRadius="xl"
                  />
                </FormControl>
              )}

              {(nextStatus === "return_in_transit" ||
                nextStatus === "returned_to_sender") && (
                <FormControl>
                  <FormLabel>Return reason</FormLabel>
                  <Input
                    placeholder="return_to_sender"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    borderRadius="xl"
                  />
                </FormControl>
              )}

              {!overrideTransition &&
                normalizeStatus(selectedJob?.status) ===
                  "proof_of_delivery_pending" &&
                nextStatus === "delivered" && (
                  <FormControl isRequired>
                    <FormLabel>Delivery PIN</FormLabel>
                    <Input
                      placeholder="Enter PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      borderRadius="xl"
                    />
                    <FormHelperText>
                      Use the same customer delivery PIN that was generated for
                      this job.
                    </FormHelperText>
                  </FormControl>
                )}

              {selectedJob?.cashOnDelivery && nextStatus === "delivered" && (
                <>
                  <Checkbox
                    isChecked={cashCollected}
                    onChange={(e) => setCashCollected(e.target.checked)}
                  >
                    Cash collected
                  </Checkbox>

                  <FormControl>
                    <FormLabel>Cash collected amount</FormLabel>
                    <Input
                      type="number"
                      placeholder="0"
                      value={cashCollectedAmount}
                      onChange={(e) => setCashCollectedAmount(e.target.value)}
                      borderRadius="xl"
                    />
                  </FormControl>
                </>
              )}

              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Input
                  placeholder="Optional note"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  borderRadius="xl"
                />
              </FormControl>

              <Box
                borderWidth="1px"
                borderColor="red.200"
                borderRadius="xl"
                bg="red.50"
                p={4}
              >
                <Text fontWeight="semibold" color="red.700" mb={2}>
                  Delete dispatch job
                </Text>
                <Text fontSize="sm" color="red.700" mb={3}>
                  This is intended for admin cleanup. Type DELETE to confirm.
                </Text>
                <Input
                  placeholder="Type DELETE"
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value)}
                  borderRadius="xl"
                  bg="white"
                />
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Flex
              w="100%"
              direction={{ base: "column-reverse", md: "row" }}
              justify="space-between"
              align={{ base: "stretch", md: "center" }}
              gap={3}
            >
              <Button
                colorScheme="red"
                variant="outline"
                onClick={handleDeleteJob}
                isLoading={isDeletingJob}
                isDisabled={
                  isClaiming ||
                  isUpdatingStatus ||
                  isVerifyingPin ||
                  isUnassigningDriver
                }
              >
                Delete Job
              </Button>

              <Flex
                direction={{ base: "column", sm: "row" }}
                gap={3}
                justify={{ base: "stretch", md: "flex-end" }}
                w={{ base: "100%", md: "auto" }}
              >
                <Button
                  w={{ base: "100%", sm: "auto" }}
                  variant="ghost"
                  onClick={closeManageModal}
                  isDisabled={
                    isClaiming ||
                    isUpdatingStatus ||
                    isVerifyingPin ||
                    isDeletingJob ||
                    isUnassigningDriver
                  }
                >
                  Cancel
                </Button>
                <Button
                  w={{ base: "100%", sm: "auto" }}
                  colorScheme="blue"
                  borderRadius="full"
                  onClick={handleSaveChanges}
                  isLoading={
                    isClaiming ||
                    isUpdatingStatus ||
                    isVerifyingPin ||
                    isUnassigningDriver
                  }
                  loadingText="Saving"
                >
                  Save Changes
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const DriverAssignmentPage: React.FC = () => (
  <AdminAccessProvider>
    <DriverAssignmentPageContent />
  </AdminAccessProvider>
);

export default DriverAssignmentPage;
